import { useState, useEffect, useCallback, useMemo } from 'react';
import { capsuleConfigAPI, formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Save, Loader2, RotateCcw, Lock, Info } from 'lucide-react';
import { toast } from 'sonner';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * Qué pantalla ve cada rol, decidido por la cápsula.
 *
 * **Por qué existe esta pantalla y no alcanzaba con la API.** La matriz ya se validaba al
 * escribir, pero sólo se podía tocar con `curl`. La promesa del cambio era que darle el tablero
 * ejecutivo a un rol de consulta no exigiera tocar el repo y desplegar; exigir `curl` no es mejor
 * — es la misma barrera con otro nombre.
 *
 * **Se ve qué decidió la cápsula y qué viene del producto.** Una fila que coincide con el default
 * y una que alguien puso a mano se ven idénticas si no se dice: sin esa marca nadie puede
 * contestar «¿qué cambiamos nosotros?».
 *
 * **El admin se dibuja tildado y trabado.** Es la salida de emergencia: una cápsula que se
 * quedara sin admin en la configuración sería un error que se arregla desde la configuración a
 * la que ya no se puede entrar. Mostrarlo destildado haría creer que se lo puede sacar, y alguien
 * diseñaría el resto de los accesos sobre esa creencia.
 *
 * **Esto no protege el dato.** Cada endpoint sigue exigiendo lo suyo: esto decide qué se le
 * *ofrece* a alguien. Ofrecer lo que el backend rechaza es peor que no ofrecerlo — la persona no
 * aprende cuál es la regla, aprende que la aplicación falla.
 */
export default function PantallasPorRol({ tenantId, capsuleId }) {
  const [catalogo, setCatalogo] = useState(null);
  const [borrador, setBorrador] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    if (!tenantId || !capsuleId) return;
    setCargando(true);
    try {
      const { data } = await capsuleConfigAPI.pantallas(tenantId, capsuleId);
      setCatalogo(data);
      setBorrador(Object.fromEntries((data.pantallas || []).map((p) => [p.id, p.roles])));
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo leer quién ve qué');
    } finally {
      setCargando(false);
    }
  }, [tenantId, capsuleId]);

  useEffect(() => { cargar(); }, [cargar]);

  const original = useMemo(
    () => Object.fromEntries((catalogo?.pantallas || []).map((p) => [p.id, p.roles])),
    [catalogo],
  );
  const sucio = useMemo(
    () => JSON.stringify(borrador) !== JSON.stringify(original),
    [borrador, original],
  );

  const admin = catalogo?.rol_que_siempre_entra || 'admin';

  const alternar = (pantallaId, rol, tildado) =>
    setBorrador((prev) => {
      const actuales = new Set(prev[pantallaId] || []);
      if (tildado) actuales.add(rol); else actuales.delete(rol);
      actuales.add(admin);                      // nunca se puede sacar
      const orden = (catalogo?.roles || []).map((r) => r.id);
      return { ...prev, [pantallaId]: orden.filter((r) => actuales.has(r)) };
    });

  const volverAlDefault = (p) =>
    setBorrador((prev) => ({ ...prev, [p.id]: p.por_omision }));

  const guardar = async () => {
    setGuardando(true);
    try {
      // **Sólo lo que difiere del default viaja.** Guardar las quince filas convertiría cada
      // una en "decidida acá", y a partir de ahí una mejora del producto no llegaría nunca:
      // quedarían congeladas contra el default del día que alguien apretó Guardar.
      const declarado = {};
      for (const p of catalogo.pantallas) {
        const roles = borrador[p.id] || [];
        const igual = JSON.stringify(roles) === JSON.stringify(p.por_omision);
        if (!igual) declarado[p.id] = roles;
      }
      await capsuleConfigAPI.update(tenantId, capsuleId, { pantallas_por_rol: declarado });
      toast.success('Listo. Cada persona lo ve al volver a entrar.');
      await cargar();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <p className="text-xs text-zinc-500 flex items-center gap-2 py-6 justify-center">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Leyendo quién ve qué…
      </p>
    );
  }
  if (!catalogo) return null;

  const roles = catalogo.roles || [];
  const decididas = (catalogo.pantallas || []).filter((p) => p.decidida_en_la_capsula).length;

  return (
    <div className="space-y-3" data-testid="pantallas-por-rol">
      <QueSeConfiguraAca
        testId="ayuda-pantallas"
        queEs="Qué pantallas de la consola ve cada rol en esta cápsula."
        paraQue="Antes esto estaba escrito a mano en el código, así que darle el tablero ejecutivo a alguien de consulta exigía tocar el repo y desplegar. Ahora es una decisión de cada organización."
        aQueAfecta={<>Al <b>menú</b> y a lo que cada persona puede abrir. <b>No protege el dato</b>: cada operación sigue pidiendo su permiso por separado. Esto decide qué se le <i>ofrece</i> a alguien — ofrecer algo que el backend después rechaza es peor que no ofrecerlo, porque la persona no aprende la regla, aprende que la aplicación falla.</>}
        ejemplo={<>Darle «Ejecutivo» al rol operador para que vea el tablero en modo lectura. Lo que no toques <b>lo sigue decidiendo el producto</b> y se mantiene al día si el default cambia: por eso sólo se guarda lo que difiere.</>}
        ojo={<>El administrador entra siempre y no se puede sacar: sin esa salida, una fila mal escrita deja a la organización sin poder entrar a arreglarla — un error de configuración que se corrige desde la configuración a la que ya no se puede entrar.</>}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="max-w-2xl space-y-1">
          <p className="text-[10px] text-zinc-500">
            Qué pantallas de la consola ve cada rol en esta cápsula. Lo que no se toca
            <b> lo decide el producto</b>, y sigue al día si el default cambia.
          </p>
          <p className="text-[10px] text-zinc-500 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              Esto decide <b>qué se le ofrece</b> a alguien, no a qué dato llega: cada operación
              sigue pidiendo su permiso por separado.
            </span>
          </p>
        </div>
        <Badge variant="secondary" className="text-[9px] rounded-sm shrink-0">
          {decididas} decididas acá
        </Badge>
      </div>

      <Card className="rounded-sm border-brand-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-brand-border bg-zinc-50/60">
                <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-600">
                  Pantalla
                </th>
                {roles.map((r) => (
                  <th key={r.id} className="px-2 py-2 text-[10px] font-semibold text-zinc-600 text-center whitespace-nowrap">
                    {r.nombre}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {catalogo.pantallas.map((p) => {
                const actuales = borrador[p.id] || [];
                const esDefault = JSON.stringify(actuales) === JSON.stringify(p.por_omision);
                return (
                  <tr key={p.id} className="border-b border-brand-border/60 last:border-0 hover:bg-zinc-50/40">
                    <td className="px-3 py-1.5">
                      <span className="font-medium">{p.nombre}</span>
                      {!esDefault && (
                        <span className="ml-1.5 text-[9px] text-brand-primary">· decidida acá</span>
                      )}
                    </td>
                    {roles.map((r) => {
                      const esAdmin = r.id === admin;
                      return (
                        <td key={r.id} className="px-2 py-1.5 text-center">
                          <Checkbox
                            data-testid={`pantalla-${p.id}-${r.id}`}
                            checked={esAdmin || actuales.includes(r.id)}
                            disabled={esAdmin}
                            onCheckedChange={(v) => alternar(p.id, r.id, v === true)}
                            title={esAdmin ? 'El administrador entra siempre: es la salida de emergencia' : ''}
                            className="rounded-[2px]"
                          />
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-right">
                      {!esDefault && (
                        <button
                          type="button"
                          onClick={() => volverAlDefault(p)}
                          data-testid={`default-${p.id}`}
                          title={`Volver a lo que decide el producto: ${p.por_omision.join(', ')}`}
                          className="text-zinc-400 hover:text-brand-primary"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          data-testid="guardar-pantallas"
          onClick={guardar}
          disabled={guardando || !sucio}
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8"
        >
          {guardando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Guardar
        </Button>
        {sucio && (
          <button type="button" onClick={cargar}
                  className="text-[10px] text-zinc-500 hover:text-brand-primary">
            descartar los cambios
          </button>
        )}
        <span className="text-[9px] text-zinc-400 flex items-center gap-1 ml-auto">
          <Lock className="w-2.5 h-2.5" /> el administrador entra siempre
        </span>
      </div>
    </div>
  );
}
